<template>
  <TabbedBox :useQueryNavigation="true">
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-margin-list">
        <i class="far fa-list"></i> Order Margin List
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-margin-list">
        <DataTable
          ref="dataTable"
          :columns="columnList"
          defaultColumnOrderDir="DESC"
          :defaultColumnOrderIndex="7"
          :url="dataURL"
          :filterOptions="orderMarginFilterOptions"
          :displayTools="true"
          :hasDateRangeFilter="true"
          :displayExportTools="true"
          :useReportingServer="true"
          :blockFiltersWhileLoading="true"
          exportTitle="Orders"
          :debounceTime="2000"
        >
          <template v-slot:title>
            <ContentTitle>Order Margin Report</ContentTitle>
            <ContentSubTitle>Shows the estimated costs, actual costs and sales margin for completed orders</ContentSubTitle>
            <ContentSubTitle>NCRs are factored into source order costs &amp; totals</ContentSubTitle>
          </template>
          <template v-slot:tools>
            <slot name="tools" />
          </template>
          <template v-slot:row="row">
            <tr :key="row.data.id">
              <td class="table-data-nav-link">
                <LinkOrder :name="row.data.sequenceNumber ? '#'+row.data.sequenceNumber : 'Pending'" :id="row.data.id" />
              </td>
              <td>
                <LabelBadge :labelClass="`label-order-type-${row.data.type}`">{{row.data.type | formatLookupName(OrderTypeLookup)}}</LabelBadge>
              </td>
              <td>
                <LabelOrderStatusBadge :status="row.data.status" />
              </td>
              <td>
                <LinkContact :name="row.data.contactName" :id="row.data.contactId" />
              </td>
              <td>
                <LabelAccountLocationBadge :accountLocationId="row.data.accountLocationId" />
              </td>
              <td>{{ row.data.customerRef }}</td>
              <td>
                <LabelBadge labelClass="label-despatch-badge">{{row.data.despatchTaskTypeName}}</LabelBadge>
              </td>
              <td>{{formatDate(row.data.dateOrderCreated)}}</td>
              <td>{{formatDate(row.data.dateClosed)}}</td>

              <td>{{formatCurrency(row.data.totalEstimatedValue)}}</td>
              <td>
                <span v-if="row.data.calculatedActualNCRCosts == 0">{{formatCurrency(row.data.totalActualValue)}}</span>
                <Tooltip v-else :label="`Includes NCR costs of ${formatCurrency(row.data.calculatedActualNCRCosts)}`" helpStyle><span class="font-weight-bold">{{formatCurrency(row.data.totalActualValue)}}</span></Tooltip>
              </td>
              <td>{{formatCurrency(row.data.totalVariance)}}</td>
              <td>
                <span v-if="row.data.calculatedActualNCRSubTotal == 0">{{formatCurrency(row.data.subTotal)}}</span>
                <Tooltip v-else :label="`Includes NCR adjustment of ${formatCurrency(row.data.calculatedActualNCRSubTotal)}`" helpStyle><span class="font-weight-bold">{{formatCurrency(row.data.subTotal)}}</span></Tooltip>
              </td>
              <td>{{formatCurrency(row.data.totalMargin)}}</td>
              <td>{{formatPercent(row.data.estimatedGrossProfitPercent)}}</td>
              <td>{{formatPercent(row.data.actualGrossProfitPercent)}}</td>
            </tr>
          </template>
          <template v-slot:summary="summary">
            <tr key="summary">
              <td>Totals</td>
              <td colspan="8"></td>
              <td>{{formatCurrency(summary.data.totalSummaryEstimatedValue)}}</td>
              <td>{{formatCurrency(summary.data.totalSummaryActualValue)}}</td>
              <td>{{formatCurrency(summary.data.totalSummaryVariance)}}</td>
              <td>{{formatCurrency(summary.data.totalSummarySubTotal)}}</td>
              <td>{{formatCurrency(summary.data.totalSummaryMargin)}}</td>
              <td></td>
              <td></td>
            </tr>
          </template>
        </DataTable>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import { mapState } from 'vuex'

import Utils from '@/utils/utils'
import LabelOrderStatusBadge from '@/modules/orders/components/labelOrderStatusBadge'

import { OrderTypeLookup, OrderTypes, OrderPaymentTypeLookup, OrderStatusTypes, OrderStatusTypeLookup } from '@/store/types'

export default {
  name: 'OrderMargins',
  components: {
    LabelOrderStatusBadge
  },
  data() {
    return {
      taskTypeList: [],
      columnList: [
        { title: "Order", data: "sequenceNumber" },
        { title: "Type", data: "type" },
        { title: "Status", data: "status" },
        { title: "Contact", data: "contactName" },
        { title: "Location", data: "accountLocationName" },
        { title: "Reference No.", data: "customerRef" },
        { title: "Despatch", data: "despatchTaskTypeName" },
        { title: "Created", data: "dateOrderCreated", dateFilter: true, type: 'datetime' },
        { title: "Completed", data: "dateClosed", dateFilter: true, type: 'datetime' },

        { title: "Estimated Cost", data: "totalEstimatedValue", type: 'currency', totalField: 'totalSummaryEstimatedValue' },
        { title: "Actual Cost", data: "totalActualValue", type: 'currency', totalField: 'totalSummaryActualValue' },
        { title: "Cost Variance", data: "totalVariance", type: 'currency', totalField: 'totalSummaryVariance' },

        { title: "Order Sub Total", data: "subTotal", type: 'currency', totalField: 'totalSummarySubTotal' },
        { title: "Sales Margin", data: "totalMargin", type: 'currency', totalField: 'totalSummaryMargin' },

        { title: "Estimated GP", data: "estimatedGrossProfitPercent", type: 'percent' },
        { title: "Actual GP", data: "actualGrossProfitPercent", type: 'percent' },
      ]
    }
  },
  computed: {
    ...mapState({
      taxName: state => state.account.salesTaxRate.name,
      accountLocations: state => state.account.accountLocations
    }),
    dataURL() {
      var params = {
        filter: {},
        includesummary: true
      }      

      return '/intelligence/orders/ordermargins?' + $.param(params)
    },
    orderMarginFilterOptions() {
      var filterOptions = [
        {
          field: 'type',
          name: 'Type',
          columnIndex: 0,
          options: [
            // Note: NCRs disabled, they are applied internally to source orders
            { name: 'Quote', value: OrderTypes.quote, class: 'label-order-type-quote' },
            { name: 'Order', value: OrderTypes.order, class: 'label-order-type-order' }
          ]
        },
        {
          name: 'Account Location',
          field: 'accountLocationId',
          columnIndex: 0,
          options: this.accountLocations.map(location => {
            return {
              name: location.name,
              value: location.id,
              class: 'label-misc'
            }
          })
        },
        {
          field: 'despatchTaskTypeId',
          name: 'Despatch Task Type',
          columnIndex: 0,
          options: this.taskTypeList.map(taskType => {
            return {
              name: taskType.name,
              value: taskType.id,
              class: 'label-despatch-badge'
            }
          })
        },
        {
          field: 'status',
          name: 'Status',
          columnIndex: 1,
          options: [
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.draft), value: OrderStatusTypes.draft, class: 'label-order-status-draft' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.pendingAccountApproval), value: OrderStatusTypes.pendingAccountApproval, class: 'label-order-status-pendingAccountApproval' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.pendingProductionApproval), value: OrderStatusTypes.pendingProductionApproval, class: 'label-order-status-pendingProductionApproval' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.quotePendingSent), value: OrderStatusTypes.quotePendingSent, class: 'label-order-status-quotePendingSent' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.pendingCustomerApproval), value: OrderStatusTypes.pendingCustomerApproval, class: 'label-order-status-pendingCustomerApproval' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.pendingPrePaid), value: OrderStatusTypes.pendingPrePaid, class: 'label-order-status-pendingPrePaid' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.pendingExternalSetup), value: OrderStatusTypes.pendingExternalSetup, class: 'label-order-status-pendingExternalSetup' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.preProduction), value: OrderStatusTypes.preProduction, class: 'label-order-status-preProduction' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.production), value: OrderStatusTypes.production, class: 'label-order-status-production' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.pendingCODPaid), value: OrderStatusTypes.pendingCODPaid, class: 'label-order-status-pendingCODPaid' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.despatch), value: OrderStatusTypes.despatch, class: 'label-order-status-despatch' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.ncrPendingReturn), value: OrderStatusTypes.ncrPendingReturn, class: 'label-order-status-ncrPendingReturn' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.completed), value: OrderStatusTypes.completed, class: 'label-order-status-completed' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.denied), value: OrderStatusTypes.denied, class: 'label-order-status-denied' },
            { name: Utils.getLookupName(OrderStatusTypeLookup, OrderStatusTypes.cancelled), value: OrderStatusTypes.cancelled, class: 'label-order-status-cancelled' }            
          ]
        }
      ]

      return filterOptions
    }
  },
  created() {
    this.OrderTypeLookup = OrderTypeLookup
    this.OrderTypes = OrderTypes
    this.OrderPaymentTypeLookup = OrderPaymentTypeLookup
  },
  mounted() {
    this.requestData()
  },
  beforeDestroy() {
  },
  methods: {
    requestData() {
      this.$apiService.tasksService.getTaskTypeList({ filter: { isActive: 1 }})
        .then(response => {
          this.taskTypeList = response.data
        })
        .catch(error => {
          console.error(error)
        })
    }
    
  }
}
</script>
